'use client';

import { cva, cx, type VariantProps } from 'class-variance-authority';
import {
  Button as AriaButton,
  Link as AriaLink,
  composeRenderProps,
  type ButtonProps as AriaButtonProps,
  type ButtonRenderProps,
  type LinkProps as AriaLinkProps,
  type LinkRenderProps,
} from 'react-aria-components';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { mergeProps } from 'react-aria';
import { AnimatePresence, motion, type Transition } from 'motion/react';
import type { ReactNode } from 'react';
import { IconPropsContext } from '@cbhq/bds-web/components/icon';
import { createContextProps } from '@cbhq/bds-web/utils/create-context-props';
import { useContextProps } from '@cbhq/bds-web/hooks/use-context-props';
import { Spinner } from '../spinner/spinner';
import { CircleCheckIcon } from '@cbhq/bds-web/icons/2/circle-check-icon';

const transition: Transition = { duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] };

export const buttonStyles = cva(
  [
    'flex group items-center data-focus-visible:outline-offset-0 justify-center',
    '[:where(&)]:rounded-full',
    'select-none cursor-pointer data-disabled:cursor-default',
    'transition-[color,background-color,box-shadow,padding,opacity] duration-150 data-focus-visible:transition-none',
  ],
  {
    variants: {
      size: {
        sm: '[:where(&)]:gap-0.5 [:where(&)]:text-label-medium',
        md: '[:where(&)]:gap-0.5 [:where(&)]:text-headline',
        lg: '[:where(&)]:gap-1 [:where(&)]:text-headline',
      },
      variant: {
        outline: ['ring-bds-gray-10 ring-inset ring-1'],
        'outline-negative': ['ring-bds-red-50/30 ring-inset ring-1'],
        secondary: [],
        primary: [],
        negative: [],
        ghost: [],
        bare: [],
      },
      status: {
        idle: null,
        pending: null,
        success: null,
      },
      iconOnly: {
        true: 'aspect-square',
        false: '[:where(&)]:w-fit',
      },
      hasStartIcon: {
        true: null,
        false: null,
      },
      hasEndIcon: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      // Size heights (excluded for bare)
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        size: 'sm',
        className: '[:where(&)]:h-8',
      },
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        size: 'md',
        className: '[:where(&)]:h-9',
      },
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        size: 'lg',
        className: '[:where(&)]:h-12',
      },
      // Bare height — always 1 line-height
      {
        variant: 'bare',
        className: '[:where(&)]:h-lh',
      },

      // Size + iconOnly padding (excluded for bare)
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        size: 'sm',
        iconOnly: false,
        className: '[:where(&)]:px-3',
      },
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        size: 'md',
        iconOnly: false,
        className: '[:where(&)]:px-3.5',
      },
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        size: 'lg',
        iconOnly: false,
        className: '[:where(&)]:px-4',
      },
      // Start/end icon padding (excluded for bare)
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        hasStartIcon: true,
        className: '[:where(&)]:pl-2',
      },
      {
        variant: ['outline', 'outline-negative', 'secondary', 'primary', 'negative', 'ghost'],
        hasEndIcon: true,
        className: '[:where(&)]:pr-2',
      },

      // Disabled opacity only applies in idle — pending/success have their own visual treatment
      { status: 'idle', className: 'data-disabled:opacity-30' },

      // Idle states — default bg/text/hover for each variant
      {
        variant: 'outline',
        status: 'idle',
        className:
          'bg-transparent dark:bg-bds-gray-100/4 text-bds-gray-80 dark:ring-bds-gray-100/7 data-hovered:bg-bds-gray-5 data-hovered:ring-bds-gray-10 data-pressed:bg-bds-gray-5 data-pressed:ring-bds-gray-10',
      },
      {
        variant: 'outline-negative',
        status: 'idle',
        className:
          'bg-transparent text-bds-red-50 data-hovered:bg-bds-red-50/5 data-hovered:ring-bds-red-50/30 data-pressed:bg-bds-red-50/5 data-pressed:ring-bds-red-50/30',
      },
      {
        variant: 'secondary',
        status: 'idle',
        className:
          'bg-bds-gray-10 text-bds-gray-80 data-hovered:bg-bds-gray-15 data-pressed:bg-bds-gray-15',
      },
      {
        variant: 'primary',
        status: 'idle',
        className:
          '[:where(&)]:bg-bds-gray-80 text-bds-gray-0 data-hovered:[:where(&)]:bg-bds-gray-70 data-pressed:[:where(&)]:bg-bds-gray-70',
      },
      {
        variant: 'negative',
        status: 'idle',
        className:
          'bg-bds-red-50 text-bds-gray-0 data-hovered:bg-bds-red-60 data-pressed:bg-bds-red-60',
      },
      {
        variant: 'ghost',
        status: 'idle',
        className: 'text-bds-gray-80 data-hovered:bg-bds-gray-5 data-pressed:bg-bds-gray-5',
      },
      {
        variant: 'bare',
        status: 'idle',
        className: '[:where(&)]:text-bds-gray-80',
      },

      // Pending states — muted palette
      {
        variant: 'ghost',
        status: 'pending',
        className: 'bg-bds-gray-10 text-bds-gray-50',
      },
      {
        variant: 'bare',
        status: 'pending',
        className: 'text-bds-gray-50',
      },
      {
        variant: 'outline',
        status: 'pending',
        className: 'bg-bds-gray-10 text-bds-gray-50 ring-0',
      },
      {
        variant: 'outline-negative',
        status: 'pending',
        className: 'bg-bds-red-5 text-bds-red-50 ring-0',
      },
      {
        variant: 'secondary',
        status: 'pending',
        className: 'bg-bds-gray-10 text-bds-gray-50',
      },
      {
        variant: 'primary',
        status: 'pending',
        className: 'bg-bds-gray-10 text-bds-gray-50',
      },
      {
        variant: 'negative',
        status: 'pending',
        className: 'bg-bds-red-5 text-bds-red-50',
      },

      // Success states — same muted bg, stronger text
      {
        variant: 'ghost',
        status: 'success',
        className: 'bg-bds-gray-10 text-bds-gray-80',
      },
      {
        variant: 'bare',
        status: 'success',
        className: 'text-bds-gray-80',
      },
      {
        variant: 'outline',
        status: 'success',
        className: 'bg-bds-gray-10 text-bds-gray-80 ring-0',
      },
      {
        variant: 'outline-negative',
        status: 'success',
        className: 'bg-bds-red-5 text-bds-red-50 ring-0',
      },
      {
        variant: 'secondary',
        status: 'success',
        className: 'bg-bds-gray-10 text-bds-gray-80',
      },
      {
        variant: 'primary',
        status: 'success',
        className: 'bg-bds-gray-10 text-bds-gray-80',
      },

      {
        variant: 'negative',
        status: 'success',
        className: 'bg-bds-red-5 text-bds-red-50',
      },
    ],
  }
);

export type { ButtonRenderProps, LinkRenderProps };

type ButtonVariantProps = Pick<
  NonNullable<VariantProps<typeof buttonStyles>>,
  'variant' | 'size'
> & {
  status?: 'idle' | 'pending' | 'success';
};

type ButtonAsButton = Omit<AriaButtonProps, 'isPending'> &
  ButtonVariantProps & {
    ref?: React.Ref<HTMLButtonElement>;
    startIcon?: ReactNode | ((renderProps: ButtonRenderProps) => ReactNode);
    endIcon?: ReactNode | ((renderProps: ButtonRenderProps) => ReactNode);
    href?: never;
  };

type ButtonAsLink = Omit<AriaLinkProps, 'children' | 'className' | 'style'> &
  ButtonVariantProps &
  Pick<NextLinkProps, 'replace' | 'scroll' | 'prefetch'> & {
    ref?: React.Ref<HTMLAnchorElement>;
    href: string;
    children?: AriaLinkProps['children'];
    className?: AriaButtonProps['className'];
    startIcon?: ReactNode | ((renderProps: LinkRenderProps) => ReactNode);
    endIcon?: ReactNode | ((renderProps: LinkRenderProps) => ReactNode);
    style?: AriaButtonProps['style'];
  };

export type { ButtonAsButton as ButtonAsButtonProps, ButtonAsLink as ButtonAsLinkProps };
export type ButtonProps = ButtonAsButton | ButtonAsLink;

export const ButtonPropsContext = createContextProps<ButtonProps>();

function ButtonContent({
  status,
  startIcon,
  endIcon,
  iconOnly,
  iconSize,
  children,
}: {
  status: 'idle' | 'pending' | 'success';
  startIcon: ReactNode;
  endIcon: ReactNode;
  iconOnly: boolean;
  iconSize: string;
  children: ReactNode;
}) {
  const iconSlotClassName = cx(
    !iconOnly && iconSize,
    'shrink-0 inline-flex items-center justify-center',
    'h-0 overflow-visible'
  );

  return (
    <IconPropsContext value={{ className: iconSize }}>
      <AnimatePresence mode="wait" initial={false}>
        {status === 'pending' ? (
          <motion.span
            key="spinner"
            className={iconSlotClassName}
            initial={{ opacity: 0, scale: 0.8, filter: 'blur(2px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.8, filter: 'blur(2px)' }}
            transition={transition}
          >
            <Spinner />
          </motion.span>
        ) : status === 'success' ? (
          <motion.span
            key="check"
            className={iconSlotClassName}
            initial={{ opacity: 0, scale: 0.8, filter: 'blur(2px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.8, filter: 'blur(2px)' }}
            transition={transition}
          >
            <CircleCheckIcon />
          </motion.span>
        ) : startIcon ? (
          <motion.span
            key="start-icon"
            className={iconSlotClassName}
            initial={{ opacity: 0, scale: 0.8, filter: 'blur(2px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.8, filter: 'blur(2px)' }}
            transition={transition}
          >
            {startIcon}
          </motion.span>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {status !== 'success' && !!children && (
          <motion.span
            key="label"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transition}
            className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {children}
          </motion.span>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {status !== 'success' && endIcon && (
          <motion.span
            key="end-icon"
            className={iconSlotClassName}
            initial={{ opacity: 0, scale: 0.8, filter: 'blur(2px)', width: 0 }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)', width: 'auto' }}
            exit={{ opacity: 0, scale: 0.8, filter: 'blur(2px)', width: 0 }}
            transition={transition}
          >
            {endIcon}
          </motion.span>
        )}
      </AnimatePresence>
    </IconPropsContext>
  );
}

export function Button(originalProps: ButtonProps) {
  const resolvedProps = useContextProps(originalProps, ButtonPropsContext);
  const {
    variant = 'primary',
    size = 'md',
    status = 'idle',
    startIcon,
    endIcon,
    children,
    className: classNameProp,
  } = resolvedProps;
  const iconOnly = !!startIcon && !endIcon && !children;
  const hasStartIcon = !iconOnly && status !== 'success' && (status !== 'idle' || !!startIcon);
  const hasEndIcon = status !== 'success' && !!endIcon;
  const iconSize = size === 'sm' ? '[:where(&)]:size-4.5' : '[:where(&)]:size-6';

  const composedClassName = composeRenderProps(classNameProp, (className) =>
    buttonStyles({
      variant,
      size,
      status,
      iconOnly,
      hasStartIcon,
      hasEndIcon,
      className,
    })
  );

  if ('href' in resolvedProps && resolvedProps.href) {
    const {
      variant: _variant,
      size: _size,
      status: _status,
      startIcon: linkStartIcon,
      endIcon: linkEndIcon,
      href,
      replace,
      scroll,
      prefetch,
      children: linkChildren,
      className: _className,
      style: _style,
      ...linkProps
    } = resolvedProps as ButtonAsLink;

    const linkClassName = buttonStyles({
      variant,
      size,
      status,
      iconOnly,
      hasStartIcon,
      hasEndIcon,
      className: typeof classNameProp === 'string' ? classNameProp : undefined,
    });

    return (
      <AriaLink
        {...mergeProps(linkProps, {
          href,
          className: linkClassName,
          render: (domProps: React.HTMLAttributes<HTMLElement>) => (
            <NextLink
              {...(domProps as React.ComponentProps<typeof NextLink>)}
              href={href}
              replace={replace}
              scroll={scroll}
              prefetch={prefetch}
            />
          ),
        })}
      >
        {composeRenderProps(linkChildren, (children, renderProps) => {
          const resolvedStartIcon =
            typeof linkStartIcon === 'function' ? linkStartIcon(renderProps) : linkStartIcon;
          const resolvedEndIcon =
            typeof linkEndIcon === 'function' ? linkEndIcon(renderProps) : linkEndIcon;

          return (
            <ButtonContent
              status={status}
              startIcon={resolvedStartIcon}
              endIcon={resolvedEndIcon}
              iconOnly={iconOnly}
              iconSize={iconSize}
            >
              {children}
            </ButtonContent>
          );
        })}
      </AriaLink>
    );
  }

  const {
    variant: _variant,
    size: _size,
    status: _status,
    startIcon: buttonStartIcon,
    endIcon: buttonEndIcon,
    children: buttonChildren,
    isDisabled,
    ...buttonProps
  } = resolvedProps as ButtonAsButton;

  return (
    <AriaButton
      isPending={status === 'pending'}
      isDisabled={status === 'idle' ? isDisabled : undefined}
      {...mergeProps(buttonProps, {
        className: composedClassName,
        children: composeRenderProps(buttonChildren, (children, renderProps) => {
          const resolvedStartIcon =
            typeof buttonStartIcon === 'function' ? buttonStartIcon(renderProps) : buttonStartIcon;
          const resolvedEndIcon =
            typeof buttonEndIcon === 'function' ? buttonEndIcon(renderProps) : buttonEndIcon;

          return (
            <ButtonContent
              status={status}
              startIcon={resolvedStartIcon}
              endIcon={resolvedEndIcon}
              iconOnly={iconOnly}
              iconSize={iconSize}
            >
              {children}
            </ButtonContent>
          );
        }),
      })}
    />
  );
}
